"use client";

import React from "react";
import { ArrowRight, Sparkles, Droplets, Sun, FlaskConical } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const heroImage = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit1.png-wvc-sizes",
    alt: "Kit Ritual Completo - Set completo de rituales de belleza natural"
  };
  const featurePills = [{
    icon: Sparkles,
    label: "Crema Facial de hongo Maitake"
  }, {
    icon: Droplets,
    label: "Crema Corporal de hongo Reishi"
  }, {
    icon: Sun,
    label: "Té de hongo Shiitake"
  }, {
    icon: FlaskConical,
    label: "Tintura de hongos mixtos"
  }];
  return <section id="hero" data-nav="light" className="relative w-full bg-background text-foreground overflow-hidden pt-12 pb-16 md:pt-20 md:pb-24 lg:pt-24 lg:pb-32">
      {/* Decorative ambient gradients */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-20 h-96 w-96 rounded-full bg-accent/30 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-secondary/50 blur-3xl" />

      <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          
          {/* Left Column: Narrative & Action */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col items-start lg:col-span-6">
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 shadow-2xs">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="font-sans text-xs font-semibold tracking-wider uppercase text-foreground/80">
                Botánica &amp; micología mexicana
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.18]">
              Rituales de piel y bienestar inspirados en hongos
            </h1>

            {/* Supporting Paragraph */}
            <p className="mt-5 max-w-xl font-sans text-base leading-relaxed text-foreground/80 sm:text-lg">
              Descubre una rutina sencilla para cuidar tu piel y acompañar tu bienestar diario con maitake, reishi y shiitake.
            </p>

            {/* CTA and Secondary Link */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-sm transition-[color,background-color,box-shadow,transform] duration-200 hover:bg-primary/90 hover:-translate-y-0.5 hover:shadow-md">
                <Link to="#full-kit-destacado" className="flex items-center justify-center gap-2">
                  <span>Comprar el kit</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="rounded-full border border-border bg-secondary px-7 py-3.5 text-base font-semibold text-secondary-foreground transition-[color,background-color,border-color] duration-200 hover:bg-secondary/80">
                <Link to="/ShopPage">Ver productos</Link>
              </Button>
            </div>

            {/* Feature Pills */}
            <div className="mt-10 pt-8 border-t border-border w-full grid grid-cols-2 md:grid-cols-4 gap-3">
              {featurePills.map((pill, index) => {
              const IconComponent = pill.icon;
              return <div key={index} data-index={index} className="flex items-center gap-2.5 rounded-xl bg-card/60 px-3 py-2 border border-border/70">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <IconComponent className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-sans text-xs font-medium text-foreground">
                      {pill.label}
                    </span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: Editorial Lifestyle Imagery */}
          <div className="relative lg:col-span-6 flex justify-center">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.1
          }} className="group relative w-full max-w-lg lg:max-w-none">
              {/* Image Container with organic pill-like curvature */}
              <div className="relative overflow-hidden rounded-3xl border border-border/80 bg-card shadow-lg aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
                <img src={heroImage.src} data-wvc-srcset={heroImage.srcset} data-wvc-sizes={heroImage.sizes} alt={heroImage.alt} loading="eager" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />

                {/* Ambient photo overlay */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-60" />

                {/* Floating Ritual Capsule Info */}
                <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6 rounded-2xl border border-border/60 bg-card/90 p-4 sm:p-5 backdrop-blur-md shadow-md">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-sans text-xs font-semibold uppercase tracking-wider text-primary">
                        Ritual completo
                      </p>
                      <h2 className="mt-0.5 font-serif text-base sm:text-lg font-medium text-card-foreground">
                        Kit Ritual Completo
                      </h2>
                      <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                        Rostro, cuerpo e interior en un solo kit
                      </p>
                    </div>
                    <div className="shrink-0">
                      <span className="inline-flex items-center justify-center rounded-full bg-accent px-3 py-1 font-sans text-xs font-semibold text-accent-foreground border border-primary/20">
                        100% natural
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative botanical badge */}
              <div aria-hidden="true" className="hidden sm:flex absolute -top-4 -right-4 h-20 w-20 items-center justify-center rounded-full bg-secondary border border-border shadow-md">
                <div className="text-center">
                  <span className="block font-serif text-sm font-semibold italic text-secondary-foreground">
                    Hecho en
                  </span>
                  <span className="block font-sans text-[10px] font-bold tracking-wider uppercase text-secondary-foreground">
                    México
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}